import {inject, Injectable} from '@angular/core';
import {liquidityPoolOrderEntryFacetAbi} from 'dex-abi/contracts/LiquidityPoolOrderEntryFacet';
import {EstimateContractGasParameters, Hash, Hex, toHex} from 'viem';
import {CommonBlockchainUtilService} from '../../../components/dex/common-blockchain-util.service';
import {DEX_NONE_VALUE} from '../../../components/dex/dex-streaming/const';
import {ComputedTranche} from '../../../components/dex/dex-streaming/types/computed/computed-tranche';
import {Deliverable} from '../../../components/dex/dex-streaming/types/deliverable';
import {vToDec} from '../../../components/dex/dex-streaming/utils';
import {Erc20BalancesService} from '../../../components/dex/dyn-balances/erc20-balances.service';
import {DynWallet} from '../../../components/dynamic-xyz/dyn-xyz-wc/dyn-remote';
import {DynamicZyxDaemonService} from '../../../components/dynamic-xyz/dynamic-zyx-daemon.service';
import {isDynamicEmbeddedWallet} from '../../../components/dynamic-xyz/utils/dyn-xyz-utils';
import {LoggerService} from '../../../components/logger/logger.service';
import {AppConfigService} from '../../../services/config.service';
import {UserService} from '../../../services/user.service';
import {TxHistoryRecFactory} from '../../dex-tx-history-widget/tx-history-rec.factory';
import {TxApiStorageMiddlewareService} from '../../dex-tx-history-widget/tx-storage/tx-api-storage-middleware.service';
import {DexKnownError} from './dex-known-errors';
import {DexTradeBgExecService} from './dex-trade-bg-exec.service';
import {DexTxQueueService} from './dex-tx-queue.service';

interface TimeLogger {
    timerName: string;
    log: (name: string, data?: any) => void;
    end: (data?: any) => void;
}

const EMPTY_CLIENT_REFERENCE: Hex = '0x0000000000000000000000000000000000000000000000000000000000000000';

const orderEntryAbi = liquidityPoolOrderEntryFacetAbi;
type OrderEntryAbi = typeof orderEntryAbi;

@Injectable()
export class DexTradeService {

    private static timerCounter: number = 0;

    private readonly txHistoryRecFactory = inject(TxHistoryRecFactory);
    private readonly appConfigService = inject(AppConfigService);
    private readonly erc20BalancesService = inject(Erc20BalancesService);
    private readonly txStorageService = inject(TxApiStorageMiddlewareService);
    private readonly commonBlockchainUtilService = inject(CommonBlockchainUtilService);
    private readonly loggerService = inject(LoggerService);
    private readonly userService = inject(UserService);
    private readonly dynamicZyxDaemonService = inject(DynamicZyxDaemonService);
    private readonly dexTradeBgExecService = inject(DexTradeBgExecService);
    private readonly dexTxQueueService = inject(DexTxQueueService);

    private readonly dexAddr = this.getDexAddr();

    public init(): void {
        (window as any).dexTradeTestService = this; // TODO

        // Removed @dynamic-labs-sdk/client
        // (window as any).dexDynClient = () => this.testDynClient();
        // (window as any).dexGetWalletProvidersData = (client?: DynamicClient) => {
        //     const walletProvidersData = getAvailableWalletProvidersData(client);
        //
        //     return walletProvidersData.map((walletProviderData) => {
        //         return {
        //             displayName: walletProviderData.metadata.displayName,
        //             walletProviderData,
        //             act:         connectAndVerifyWithWalletProvider({walletProviderKey: walletProviderData.key})
        //         };
        //     });
        // };
        // (window as any).dexGetWalletAccount = (client?: DynamicClient) => {
        //     return getWalletAccounts(client);
        // };
        // (window as any).dexFindWalletAccount = async () => {
        //     const walletAccount = await getWalletAccountFromAddress({
        //         address: this.dynamicZyxDaemonService.dynRemote.primaryWallet$.value.address,
        //         chain: this.appConfigService.appConfig.dex.chain.id as any // It requires Enum, not ID number
        //     });
        //     console.log(walletAccount);
        // };
    }

    public async queuedRequestAllowance(params: {
        tr: ComputedTranche,
        d: Deliverable,
        dQty: bigint,
        wallet: DynWallet
    }): Promise<void> {
        const requestAllowance = async () => {
            const tokenQty = await this.erc20BalancesService.convertDelUnitsToToken(params.d, params.dQty);
            const walletClient = await params.wallet.getWalletClient();

            try {
                await this.erc20BalancesService.approveSpending({
                    d: params.d,
                    tokenQty,
                    owner: params.wallet.address,
                    spender: this.dexAddr,
                    walletClient
                });
            } catch (error: any) {
                throw await DexKnownError.fromViem('Allowance', error);
            }
        };

        await this.dexTxQueueService.enqueue(params.wallet.address, requestAllowance);
    }

    public async estimateDepositGas(params: {
        tr: ComputedTranche,
        d: Deliverable,
        dQty: bigint,
        wallet: DynWallet
    }): Promise<bigint> {
        const publicClient = this.commonBlockchainUtilService.getPublicClient();
        // const tokenQty = await this.erc20BalancesService.convertDelUnitsToToken(params.d, params.dQty);

        const trancheId = params.tr.raw.poolTrancheId;
        const deliverableId = params.d.deliverableId;
        const assetQtyToDeposit = params.dQty;
        const minLptQty = 1;

        let gasResult: bigint | undefined = undefined;

        try {
            // TODO: Check allowance on UI

            const gasLimit = await publicClient.estimateContractGas({
                address:      this.dexAddr,
                abi:          orderEntryAbi,
                functionName: 'enterDepositOrder',
                args:         [
                    BigInt(trancheId),
                    BigInt(deliverableId),
                    assetQtyToDeposit,
                    BigInt(minLptQty),
                    EMPTY_CLIENT_REFERENCE
                ],
                account:      params.wallet.address
            });

            const gasPrice = await publicClient.getGasPrice();
            gasResult = gasLimit * gasPrice;
        } catch (error: any) {
            throw await DexKnownError.fromViem('Deposit estimateGas', error);
        }

        return gasResult;
    }

    public async deposit(params: {
        tr: ComputedTranche,
        d: Deliverable,
        dQty: bigint,
        wallet: DynWallet,
        onLateError: (error: unknown) => void,
        isLateErrorDelegatedToNotification?: boolean
    }): Promise<void> {
        const tokenQty = await this.erc20BalancesService.convertDelUnitsToToken(params.d, params.dQty);

        const dexAddress = this.dexAddr;
        const trancheId = params.tr.raw.poolTrancheId;
        const deliverableId = params.d.deliverableId;
        const assetQtyToDeposit = params.dQty;
        const minLptQty = 1;
        const clientReference = this.generateClientReference();

        const walletAddress = params.wallet.address as Hash;
        const publicClient = this.commonBlockchainUtilService.getPublicClient();
        const walletClient = await params.wallet.getWalletClient();
        const account = walletClient.account;

        const thRec = this.txHistoryRecFactory.createForNewDeposit({
            hash:    undefined,
            from:    walletAddress,
            to:      dexAddress,
            chainId: walletClient.chain.id,
            trancheId,
            deliverableId,
            assetQtyToDeposit,
            minLptQty,
            clientReference
        });

        const storeTx = async (txHashToStore: Hash) => {
            if (txHashToStore) {
                thRec.hash = txHashToStore;

                await this.txStorageService.asyncCreateTransaction(thRec).catch();
            }
        };

        const timeLogger = this.createTimeLogger('Deposit', [
            params.tr.unitDeliverable.symbol,
            vToDec(params.dQty, params.d.decimalPlaces).toString(),
            params.d.symbol
        ]);

        const createDepositTask = async (): Promise<Hash | undefined> => {
            try {
                await this.erc20BalancesService.approveSpending({
                    d:       params.d,
                    tokenQty,
                    owner:   walletAddress,
                    spender: this.dexAddr,
                    walletClient
                });
                timeLogger.log('approveSpending');

                const args: EstimateContractGasParameters<OrderEntryAbi, 'enterDepositOrder'>['args'] = [
                    BigInt(trancheId), // poolTrancheId
                    BigInt(deliverableId), // assetDeliverableId
                    assetQtyToDeposit, // assetQuantityToDeposit
                    BigInt(minLptQty), // minAcceptableLptQuantityMinted
                    clientReference // clientReference
                ] as const;

                // TODO: Add simulateContract

                const estimatedGas = await publicClient.estimateContractGas({
                    address:      dexAddress,
                    abi:          orderEntryAbi,
                    functionName: 'enterDepositOrder',
                    args,
                    account
                });
                timeLogger.log('estimateContractGas');

                // +25% extra, usually 20% is enough.
                const gasLimit = (estimatedGas * 125n) / 100n;

                const txHash = await walletClient.writeContract({
                    address:      dexAddress,
                    abi:          orderEntryAbi,
                    functionName: 'enterDepositOrder',
                    args,
                    account,
                    chain:        walletClient.chain,
                    gas:          gasLimit
                });
                timeLogger.log('enterDepositOrder');

                await storeTx(txHash);
                timeLogger.log('storeTx', txHash);

                // TODO: Check `receipt.status==='reverted'` and use `call` to get the reason.
                await publicClient.waitForTransactionReceipt({
                    hash:          txHash,
                    confirmations: 1
                });

                return txHash;
            } catch (error) {
                throw await DexKnownError.fromViem('Deposit', error);
            }
        };

        let task: Promise<Hash>;
        let resultError: unknown;

        try {
            task = this.dexTxQueueService.enqueue(params.wallet.address, () => createDepositTask());

            if (isDynamicEmbeddedWallet(params.wallet, this.dynamicZyxDaemonService.dynRemote)) {
                this.dexTradeBgExecService.registerTradeBgExec({
                    execPromise: task,
                    thRec,
                    onLateError: params.onLateError,
                    isLateErrorDelegatedToNotification: params.isLateErrorDelegatedToNotification
                });
            } else {
                await task;
            }
        } catch (error) {
            resultError = error;
        }

        timeLogger.log('endSyncTask');

        if (task) {
            task.finally(() => timeLogger.end());
        } else {
            timeLogger.end();
        }

        if (resultError) {
            throw resultError;
        }
    }

    public async estimateWithdrawGas(params: {
        tr: ComputedTranche,
        dQty: bigint,
        wallet: DynWallet
    }): Promise<bigint> {
        const publicClient = this.commonBlockchainUtilService.getPublicClient();

        const trancheId = params.tr.raw.poolTrancheId;
        const lptQuantityToBurn = params.dQty;
        const minAssetValidUsd = 0n;
        const preferredDeliverableId = DEX_NONE_VALUE;
        const minAcceptablePreferredAssetQty = 0n;
        const receiver = params.wallet.address as Hex;

        let gasResult: bigint | undefined = undefined;

        try {
            const gasLimit = await publicClient.estimateContractGas({
                address:      this.dexAddr,
                abi:          orderEntryAbi,
                functionName: 'enterWithdrawalOrder',
                args:         [
                    BigInt(trancheId),
                    lptQuantityToBurn,
                    minAssetValidUsd,
                    preferredDeliverableId,
                    minAcceptablePreferredAssetQty,
                    receiver,
                    EMPTY_CLIENT_REFERENCE
                ],
                account:      params.wallet.address
            });

            const gasPrice = await publicClient.getGasPrice();
            gasResult = gasLimit * gasPrice;
        } catch (error: any) {
            throw await DexKnownError.fromViem('Withdraw estimateGas', error);
        }

        return gasResult;
    }

    public async withdraw(params: {
        tr: ComputedTranche,
        dQty: bigint,
        wallet: DynWallet,
        onLateError: (error: unknown) => void,
        isLateErrorDelegatedToNotification?: boolean
    }): Promise<void> {
        const dexAddress = this.dexAddr;
        const trancheId = params.tr.raw.poolTrancheId;
        const dTranche = params.tr.unitDeliverable;
        const tokenQty = await this.erc20BalancesService.convertDelUnitsToToken(dTranche, params.dQty);
        const lptQuantityToBurn = params.dQty;
        const minAssetValidUsd = 0n;
        const preferredDeliverableId = DEX_NONE_VALUE;
        const minAcceptablePreferredAssetQty = 0n;
        const receiver = params.wallet.address as Hex;
        const clientReference = this.generateClientReference();

        const walletAddress = params.wallet.address as Hash;
        const publicClient = this.commonBlockchainUtilService.getPublicClient();
        const walletClient = await params.wallet.getWalletClient();
        const account = walletClient.account;

        const thRec = this.txHistoryRecFactory.createForNewWithdrawal({
            hash:    undefined,
            from:    walletAddress,
            to:      dexAddress,
            chainId: walletClient.chain.id,
            trancheId,
            lptQuantityToBurn,
            minAssetValidUsd,
            preferredDeliverableId,
            minAcceptablePreferredAssetQty,
            clientReference
        });

        const storeTx = async (txHashToStore: Hash) => {
            if (txHashToStore) {
                thRec.hash = txHashToStore;

                await this.txStorageService.asyncCreateTransaction(thRec).catch();
            }
        };

        const timeLogger = this.createTimeLogger('Withdraw', [
            dTranche.symbol,
            vToDec(params.dQty, dTranche.decimalPlaces).toString()
        ]);

        const createWithdrawTask = async (): Promise<Hash | undefined> => {
            try {
                await this.erc20BalancesService.approveSpending({
                    d:       dTranche,
                    tokenQty,
                    owner:   walletAddress,
                    spender: dexAddress,
                    walletClient
                });
                timeLogger.log('approveSpending');

                const args: EstimateContractGasParameters<OrderEntryAbi, 'enterWithdrawalOrder'>['args'] = [
                    BigInt(trancheId), // poolTrancheId
                    lptQuantityToBurn, // lptQuantityToBurn
                    minAssetValidUsd, // minAssetValidUsd
                    preferredDeliverableId, // preferredDeliverableId
                    minAcceptablePreferredAssetQty, // minAcceptablePreferredAssetQty
                    receiver, // receiver
                    clientReference // clientReference
                ] as const;

                // TODO: Add simulateContract

                const estimatedGas = await publicClient.estimateContractGas({
                    address:      dexAddress,
                    abi:          orderEntryAbi,
                    functionName: 'enterWithdrawalOrder',
                    args,
                    account
                });
                timeLogger.log('estimateContractGas');

                // +25% extra, usually 20% is enough.
                const gasLimit = (estimatedGas * 125n) / 100n;

                const txHash = await walletClient.writeContract({
                    address:      dexAddress,
                    abi:          orderEntryAbi,
                    functionName: 'enterWithdrawalOrder',
                    args,
                    account,
                    chain:        walletClient.chain,
                    gas:          gasLimit
                });
                timeLogger.log('enterWithdrawalOrder');

                await storeTx(txHash);
                timeLogger.log('storeTx', txHash);

                // TODO: Check `receipt.status==='reverted'` and use `call` to get the reason.
                await publicClient.waitForTransactionReceipt({
                    hash:          txHash,
                    confirmations: 1
                });

                return txHash;
            } catch (error) {
                throw await DexKnownError.fromViem('Withdraw', error);
            }
        };

        let task: Promise<Hash>;
        let resultError: unknown;

        try {
            task = this.dexTxQueueService.enqueue(params.wallet.address, () => createWithdrawTask());

            if (isDynamicEmbeddedWallet(params.wallet, this.dynamicZyxDaemonService.dynRemote)) {
                this.dexTradeBgExecService.registerTradeBgExec({
                    execPromise: task,
                    thRec,
                    onLateError: params.onLateError,
                    isLateErrorDelegatedToNotification: params.isLateErrorDelegatedToNotification
                });
            } else {
                await task;
            }
        } catch (error) {
            resultError = error;
        }

        timeLogger.log('endSyncTask');

        if (task) {
            task.finally(() => timeLogger.end());
        } else {
            timeLogger.end();
        }

        if (resultError) {
            throw resultError;
        }
    }

    public async testDynClient(): Promise<void> {
        // Removed @dynamic-labs-sdk/client
        // const client = createDynamicClient({
        //     environmentId: this.appConfigService.appConfig.dynamicXyz.environmentId
        // });
        //
        // onDynamicEvent({
        //     event: 'initStatusChanged',
        //     listener: (e) => {
        //         console.warn('DynClient, initStatusChanged: ', e);
        //     }
        // });
        //
        // onDynamicEvent({
        //     event: 'mfaTokenChanged',
        //     listener: (e) => {
        //         console.warn('DynClient, mfaTokenChanged: ', e);
        //     }
        // });
        //
        // onDynamicEvent({
        //     event: 'projectSettingsChanged',
        //     listener: (e) => {
        //         console.warn('DynClient, projectSettingsChanged: ', e);
        //     }
        // });
        //
        // onDynamicEvent({
        //     event: 'sessionExpiresAtChanged',
        //     listener: (e) => {
        //         console.warn('DynClient, sessionExpiresAtChanged: ', e);
        //     }
        // });
        //
        // onDynamicEvent({
        //     event: 'tokenChanged',
        //     listener: (e) => {
        //         console.warn('DynClient, tokenChanged: ', e);
        //     }
        // });
        //
        // onDynamicEvent({
        //     event: 'userChanged',
        //     listener: (e) => {
        //         console.warn('DynClient, userChanged: ', e);
        //     }
        // });
        //
        // return client;
    }

    private getDexAddr(): Hex {
        return this.appConfigService.appConfig.dex.dexContractAddress as Hex;
    }

    private createTimeLogger(timerName: string, data?: any): TimeLogger {
        const timerId = ++DexTradeService.timerCounter;
        const timeLabel = `[Time #${timerId}] ${timerName}`;
        console.time(timeLabel);

        const startTs = Date.now();
        const parts: Array<{ts: number; name: string; d: number; data?: any}> = [
            {
                ts: startTs,
                name: 'start',
                d: 0,
                data
            }
        ];

        return {
            timerName,
            log: (name, data) => {
                console.timeLog(timeLabel, name, data);

                const ts = Date.now();
                parts.push({
                    ts,
                    name,
                    d: ts - parts[parts.length - 1].ts,
                    data
                });
            },
            end: data => {
                console.timeEnd(timeLabel);

                const endTs = Date.now();
                parts.push({
                    ts: endTs,
                    name: 'end',
                    d: endTs - startTs,
                    data
                });

                this.loggerService.log({
                    timerName,
                    parts,
                    userId: this.userService.user.id
                });
            }
        };
    }

    private generateClientReference(): Hex {
        return toHex(crypto.getRandomValues(new Uint8Array(32)));
    }

}
